import { Theme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { LeftRightPositioning, MoneyAmountItem, Space, TextFieldItem } from '@/components';
import { FPSLayout } from '@/layout';
import { transfer } from '@/model/withdrawal/actions';
import { makeSelectFormData } from '@/model/withdrawal/selectors';
import { WithdrawalUIState } from '@/model/withdrawal/types';
import { BankItem, ReceiverItem, WalletItem } from '@/pages/withdrawal/components';

const useStyles = makeStyles((theme: Theme) => ({
  totalBox: {
    flex: 1,
    paddingBottom: theme.spacing(),
  },
  footer: {
    paddingTop: theme.spacing(2),
    backgroundColor: theme.colors.lightBackground,
  },
}));

const selectDemand = makeSelectFormData();

const dataTest = 'transfer';

export const TransferPage: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useStyles();
  const demand = useSelector(selectDemand);
  const { formatMessage } = useIntl();

  return (
    <FPSLayout
      data-test={dataTest && `${dataTest}-layout`}
      height={480}
      minHeight={410}
      buttons={{
        back: `${WithdrawalUIState.PREPARE}`,
        logout: <FormattedMessage id="withdrawal.logout.button.title" />,
        main: {
          submit: true,
          onAct: async (): Promise<void> => {
            const { error } = await transfer()(dispatch);
            if (!error) {
              history.push(`${WithdrawalUIState.STATUS}`);
            }
          },
          disabled:
            // prettier-ignore
            // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
            !demand?.amount?.value
            || demand.amount.hasError
            || !demand.bank
            || demand.bank.hasError
            || !demand.wallet
            || demand.wallet.hasError
            || demand.message?.hasError,
          title: <FormattedMessage id="withdrawal.pages.create.submit.title" />,
        },
      }}
      header={{ title: <FormattedMessage id="withdrawal.pages.create.title" /> }}
      footer={{
        className: classes.footer,
        component: (
          <Space direction="vertical" className={classes.totalBox}>
            <LeftRightPositioning
              left={
                <span data-test={dataTest && `${dataTest}-total-label`}>
                  <strong>
                    <FormattedMessage id="withdrawal.pages.create.fields.total.header" tagName="span" />
                  </strong>
                </span>
              }
            />
            <LeftRightPositioning
              left={
                <span data-test={dataTest && `${dataTest}-fee-label`}>
                  <FormattedMessage id="withdrawal.fields.fee.label" />
                </span>
              }
              right={
                <MoneyAmountItem
                  data-test={dataTest && `${dataTest}-fee-value`}
                  value={{ value: Math.abs(demand?.fee || 0), hasError: false }}
                  readonly
                />
              }
            />
            <LeftRightPositioning
              left={
                <span data-test={dataTest && `${dataTest}-totalAmount-label`}>
                  <FormattedMessage id="withdrawal.pages.create.fields.total.label" tagName="span" />
                </span>
              }
              right={
                <MoneyAmountItem
                  data-test={dataTest && `${dataTest}-totalAmount-value`}
                  value={{ value: (demand?.amount?.value || 0) + Math.abs(demand?.fee || 0), hasError: false }}
                  readonly
                />
              }
            />
          </Space>
        ),
      }}
    >
      <Space direction="vertical" size={2}>
        <ReceiverItem
          value={{ value: demand?.receiver, hasError: false }}
          readonly
          shortPhoneMask
          data-test={dataTest && `${dataTest}-receiver`}
        />
        <MoneyAmountItem
          data-test={dataTest && `${dataTest}-amount`}
          value={demand?.amount}
          messages={{ label: formatMessage({ id: 'withdrawal.fields.amount.label' }) }}
          readonly
        />
        <WalletItem value={demand?.wallet} readonly data-test={dataTest && `${dataTest}-wallet`} />
        <BankItem value={demand?.bank} readonly data-test={dataTest && `${dataTest}-bank`} />
        {demand?.message?.value && (
          <TextFieldItem
            data-test={dataTest && `${dataTest}-message`}
            value={demand?.message}
            messages={{ label: <FormattedMessage id="withdrawal.fields.message.label" /> }}
            readonly
          />
        )}
      </Space>
    </FPSLayout>
  );
};

export default TransferPage;
